import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

/**
 * The same conversation, typed.
 *
 * Speaking is the lower-friction path when executive function is spent, which
 * is why it is the default — but it is not always the available one. Somebody
 * on a train, in a shared flat, next to a sleeping partner, or simply not able
 * to say it out loud has the same evening and none of the same options. The
 * offer said "Type instead" for months and the button ended the conversation
 * instead of moving it, because this did not exist.
 *
 * IT KEEPS NOTHING, and that is the same promise the spoken one makes. The
 * server does store the thread — the companion API was built for a persistent
 * assistant that has never been given a UI — so this deletes it on the way out.
 * Someone typing at midnight because it hurts has not agreed to have it written
 * down, and a thread saved where nothing can list it is worse than either
 * keeping it properly or not keeping it at all.
 *
 * SAFETY IS THE SERVER'S, not this component's. The crisis check runs on the
 * message before any model call, so it fires with no key and no network; the
 * reply comes back with resources attached and this renders them. Nothing here
 * decides whether something is a crisis, which is why nothing here can get it
 * wrong.
 */

interface Turn {
  role: 'you' | 'it';
  text: string;
  resources?: { name: string; contact: string }[];
}

export function TypeItThrough({ onExit }: { onExit: () => void }): JSX.Element {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const threadRef = useRef<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  /*
   * Delete on the way out, however the way out happens — the button, or this
   * component being unmounted by navigation. A ref rather than state because
   * the cleanup runs after the last render and must see the current thread id
   * rather than the one captured when the effect was created.
   *
   * Not a guarantee, and worth saying plainly rather than pretending: this
   * covers the button and any navigation inside the app, but a hard tab close
   * mid-conversation can still leave one thread behind on the server.
   */
  const discard = useCallback(() => {
    const id = threadRef.current;
    if (!id) return;
    threadRef.current = null;
    void api.deleteChatThread(id).catch(() => {});
  }, []);

  useEffect(() => discard, [discard]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, busy]);

  async function send(): Promise<void> {
    const content = draft.trim();
    if (!content || busy) return;
    setBusy(true);
    setError(null);
    setDraft('');
    setTurns((t) => [...t, { role: 'you', text: content }]);
    try {
      /*
       * Asked for, every turn.
       *
       * The flag defaults to false and nothing ever set it, so the companion
       * opened from a journal entry knew nothing about that entry — it asked
       * who Toby was seconds after being handed a summary naming him. This is
       * the person's own writing shown back to them, in a conversation they
       * opened, whose output reaches no share link and whose thread is deleted
       * on the way out.
       */
      const r = await api.sendChat({
        threadId: threadRef.current,
        content,
        includeContext: true,
      });
      threadRef.current = r.threadId;
      setTurns((t) => [
        ...t,
        { role: 'it', text: r.message.content, resources: r.crisisResources },
      ]);
    } catch (err) {
      // The message stays on screen — it was said, and removing it would look
      // like it had never been typed. Only the reply is missing.
      setError(
        err instanceof ApiError ? err.message : "That didn't send. Have another go in a moment.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack-lg stack talk">
      <div className="row-between">
        <span className="faint">Typing it through</span>
        <button
          className="btn btn-ghost btn-compact"
          onClick={() => {
            discard();
            onExit();
          }}
        >
          I'm done
        </button>
      </div>

      {turns.length === 0 && (
        <div className="talk-question">
          <h1>Whenever you're ready</h1>
          <p className="muted">
            Type as much or as little as you want. It won't ask you to fill anything in, and nothing
            from this is kept.
          </p>
        </div>
      )}

      {/* Reuses the spoken view's own classes rather than growing a second
          vocabulary for the same thing — one conversation, two input methods.
          `talk-log-typed` only lifts the height cap: there the log sits under an
          orb and is secondary, here it IS the conversation. */}
      {turns.length > 0 && (
        <div className="stack-sm stack talk-log talk-log-typed">
          {turns.map((t, i) => (
            <div key={i}>
              <p
                className={t.role === 'you' ? 'talk-said talk-said-you' : 'talk-said'}
                style={{ whiteSpace: 'pre-wrap', margin: 0 }}
              >
                {t.text}
              </p>
              {/* Real people, surfaced by the server's own check rather than by
                  anything this component decided. */}
              {t.resources && t.resources.length > 0 && (
                <div className="stack-sm stack" style={{ marginTop: 'var(--space-3)' }}>
                  {t.resources.map((r) => (
                    <div key={r.name} className="card card-quiet">
                      <strong>{r.name}</strong>
                      <p className="muted" style={{ margin: 0 }}>
                        {r.contact}
                      </p>
                    </div>
                  ))}
                  <a className="btn btn-primary btn-block" href="/sos">
                    Open Help now
                  </a>
                </div>
              )}
            </div>
          ))}
          {busy && (
            <p className="faint">
              <span className="spinner" style={{ display: 'inline-block', verticalAlign: -4 }} />{' '}
              Thinking…
            </p>
          )}
          <div ref={endRef} />
        </div>
      )}

      {error && <div className="banner banner-caution">{error}</div>}

      <div className="stack-sm stack">
        <label className="sr-only" htmlFor="talk-typed">
          What you want to say
        </label>
        <textarea
          id="talk-typed"
          className="textarea"
          style={{ minHeight: 96 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter breaks the line. A message box that
            // needs a mouse to send is a message box that does not get used.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          autoFocus
        />
        <button
          className="btn btn-primary btn-block"
          onClick={() => void send()}
          disabled={busy || !draft.trim()}
        >
          {busy ? <span className="spinner" /> : 'Send'}
        </button>
      </div>
    </div>
  );
}
